// External libraries
import { Component, createRef } from "react";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { FieldWrapper } from "@progress/kendo-react-form";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import { Invisible, setStyle, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import {
  clearEntryFields,
  disabledSections,
  entryValues,
  formErrors,
  readOnlyEntry,
  template,
} from "../../Entry/Manage/EditEntrySignals";

export class NumericField extends Component {
  inputRef = createRef();

  constructor({
    field,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    labelVisible,
    eventIndex,
    value,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      value: value,
      valid: this.validate(value, properties?.IsRequired),
      invisible: false,
      disabled: false,
      touched: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.displayNameHorizontally = properties?.DisplayNameHorizontally;
    this.conditionalFormattingRules = properties?.ConditionalFormatting;
    this.min = properties?.Min;
    this.max = properties?.Max;
    this.visibilityRules = properties?.VisibilityRules;
    this.defaultValue = properties?.DefaultValue;
    this.parameterValues = properties?.ParameterValues;
    this.numericFormat = properties?.NumericFormat;
    this.labelVisible = labelVisible;
    this.label = properties?.Label || `${fieldName}:`;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.validationMessage = "Error: A number is required.";
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    this.handleClearFieldEffect();
    if (this.isRequired) {
      if (!this.validate(this.state.value, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }
    if (this.visibilityRules || this.conditionalFormattingRules) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.value !== nextState.value ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      this.state.touched !== nextState.touched
    );
  }

  getSnapshotBeforeUpdate(_, prevState) {
    return {
      visibilityChange: prevState.invisible && !this.state.invisible,
      valueChange: prevState.value !== this.state.value,
    };
  }

  componentDidUpdate(_, __, snapshot) {
    if (snapshot.visibilityChange) this.setDefaultValue();
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }
  //#endregion lifecycle functions

  //#region custom functions
  validate(value, isRequired) {
    return isRequired ? validators.number(value) : true;
  }

  handleClearFieldEffect() {
    this.disposeClearFieldsEffect = effect(() => {
      if (clearEntryFields.value) {
        if (this.defaultValue) {
          if (entryValues.value[this.fieldName] !== this.defaultValue) {
            this.clearField();
            this.setDefaultValue();
          }
        } else if (entryValues.value[this.fieldName]) {
          this.clearField();
        }
      }
    });
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
      });
      if (this.conditionalFormattingRules && entryValues.value) this.handleStyleEffect();
    });
  }

  handleStyleEffect() {
    setStyle(
      this.inputRef.current,
      "input",
      this.conditionalFormattingRules,
      this.state.value,
      this.fieldName
    );
  }

  clearField() {
    this.clearFormErrors();
    this.setState({ value: "" }, () => {
      this.handleFieldChange({
        e: { target: { name: this.fieldName }, value: "" },
        sectionIndex: this.sectionIndex,
        fieldIndex: this.fieldIndex,
        eventIndex: this.eventIndex,
      });
    });
  }

  setDefaultValue() {
    if (this.defaultValue) {
      this.setState({
        value: this.defaultValue,
      });
      this.handleFieldChange({
        e: { target: { name: this.fieldName }, value: this.defaultValue },
        sectionIndex: this.sectionIndex,
        fieldIndex: this.fieldIndex,
        eventIndex: this.eventIndex,
      });
    }
  }

  onChange(e) {
    this.setState(
      {
        valid: this.validate(e.value, this.isRequired),
        value: e.value,
        touched: true,
      },
      () => {
        this.updateFormErrors();
        this.handleFieldChange({
          e,
          sectionIndex: this.sectionIndex,
          fieldIndex: this.fieldIndex,
          fieldType: this.fieldType,
          eventIndex: this.eventIndex,
        });
        if (this.isStageField) validateStageStep();
      }
    );
  }

  onBlur() {
    this.setState(
      (prevState) => ({
        valid: this.validate(prevState.value, this.isRequired),
        touched: true,
      }),
      () => {
        this.updateFormErrors();
        if (this.isStageField) validateStageStep();
      }
    );
  }

  getClassName() {
    if (this.isRequired) {
      if (this.state.valid) {
        return "!k-valid";
      } else {
        return "!k-invalid";
      }
    }
  }
  //#endregion custom functions

  render() {
    if (this.state.invisible) return null;
    return (
      <Ripple>
        <div
          ref={this.inputRef}
          className="k-d-flex k-flex-col"
        >
          <FieldWrapper>
            <div
              className={this.displayNameHorizontally ? "k-d-flex k-align-items-center k-mt-5" : ""}
            >
              {/* Label to the left */}
              {this.displayNameHorizontally && this.labelVisible && (
                <Label
                  editorId={this.fieldName}
                  editorValid={this.isRequired ? this.state.valid : true}
                  style={{ marginRight: "8px", fontWeight: "bold" }}
                >
                  {this.label}
                </Label>
              )}

              {/* NumericTextBox Container*/}
              <div
                className="k-form-field-wrap"
                style={{ flex: 1 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <NumericTextBox
                    data-testid={this.fieldName}
                    className={this.getClassName()}
                    id={this.fieldName}
                    name={this.fieldName}
                    label={this.labelVisible && !this.displayNameHorizontally ? this.label : null}
                    onChange={(e) => this.onChange(e)}
                    onBlur={() => this.onBlur()}
                    value={
                      this.state.value !== null &&
                      this.state.value !== undefined &&
                      this.state.value !== ""
                        ? Number(this.state.value)
                        : null
                    }
                    valid={this.isRequired ? this.state.valid : true}
                    required={this.isRequired}
                    min={parseInt(this.min)}
                    max={parseInt(this.max)}
                    format={this.numericFormat ?? "n"}
                    readOnly={this.isPrintPreview}
                    disabled={this.isReadOnly || this.state.disabled || readOnlyEntry.value}
                  />
                  <If
                    condition={
                      this.fieldInformation !== undefined &&
                      this.fieldInformation !== "" &&
                      this.fieldInformation !== "<p></p>"
                    }
                  >
                    <TooltipPopupIcon content={this.fieldInformation} />
                  </If>
                </div>
              </div>
            </div>
            <If condition={this.hint}>
              <Hint>{this.hint}</Hint>
            </If>
            <If condition={!this.state.valid && this.state.touched}>
              <Error>{this.validationMessage}</Error>
            </If>
          </FieldWrapper>
        </div>
      </Ripple>
    );
  }
}
